import { Component, OnInit } from '@angular/core';
import { ParticipantService } from '../../services/participant.service';
import { LoadingService } from '../../services/loading.service';
import { ParticipantStatus, PARTICIPANT_STATUS_OPTIONS, getParticipantStatusLabel, getParticipantStatusCssClass } from '../../enums/ParticipantStatus';

@Component({
  selector: 'app-participants',
  templateUrl: './participants.component.html',
  styleUrl: './participants.component.scss'
})
export class ParticipantsComponent implements OnInit {
  
  public participants: any[] = [];
  public searchTerm: string = '';
  public statusFilter: string = '';
  
  // Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;
  
  // Métricas
  totalParticipants: number = 0;
  activeParticipants: number = 0;
  inactiveParticipants: number = 0;
  participantsWithoutStatus: number = 0;
  
  // Status options for dropdown
  public statusOptions = PARTICIPANT_STATUS_OPTIONS;
  public ParticipantStatus = ParticipantStatus;
  
  // Status options for dropdown with "Todos" option
  public statusDropdownOptions: any[] = [];
  
  private searchTimeout: any;
  
  constructor(
    private _participantService: ParticipantService,
    private _loadingService: LoadingService
  )
  {

  }

  ngOnInit(): void {
    // Initialize dropdown options
    this.statusDropdownOptions = [
      {label: 'Todos', value: ''},
      ...this.statusOptions.map(s => ({label: s.label, value: s.value}))
    ];
    this.loadParticipants();
  }

  loadParticipants(){
    this._loadingService.show();
    this._participantService.getPaged(this.pageNumber, this.pageSize, this.searchTerm, this.statusFilter).subscribe({
      next: (response)=> {
        if (response.isSuccess && response.data) {
          this.participants = response.data.data || [];
          this.totalRecords = response.data.totalCount || 0;
          this.calculateMetrics();
        } else {
          this.participants = [];
          this.totalRecords = 0;
        }
        this._loadingService.hide();
      },
      error: (err)=> {
        console.log("error", err);
        this.participants = [];
        this.totalRecords = 0;
        this._loadingService.hide();
      }
    });
  }

  onFilterChange(): void {
    this.pageNumber = 1;
    this.loadParticipants();
  }

  onPageChange(event: any): void {
    this.pageNumber = event.page + 1; // PrimeNG es 0-indexed
    this.pageSize = event.rows;
    this.loadParticipants();
  }

  onSearchTermChange(): void {
    // Debounce search to avoid too many API calls
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    this.searchTimeout = setTimeout(() => {
      this.onFilterChange();
    }, 500);
  }

  onStatusFilterChange(): void {
    this.onFilterChange();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = '';
    this.onFilterChange();
  }

  // Helper methods for participant status
  getStatusLabel(status: any): string {
    return getParticipantStatusLabel(status);
  }

  getStatusCssClass(status: any): string {
    return getParticipantStatusCssClass(status);
  }

  isActiveParticipant(participant: any): boolean {
    return !participant.isDeleted;
  }

  getParticipantsWithStatus(): number {
    return this.participants.filter(p => p.status !== null && p.status !== undefined).length;
  }

  getParticipantsWithoutStatus(): number {
    return this.participants.filter(p => p.status === null || p.status === undefined).length;
  }

  getInitials(firstName: string, lastName: string): string {
    return `${(firstName || '')[0]}${(lastName || '')[0]}`.toUpperCase();
  }

  /** Tono cromático determinístico para el avatar de iniciales (a-f). */
  getInitialsTone(seed: string | null | undefined): string {
    const s = (seed || '?').toUpperCase();
    const code = s.charCodeAt(0) || 0;
    const tones = ['a', 'b', 'c', 'd', 'e', 'f'];
    return tones[code % tones.length];
  }

  private calculateMetrics(): void {
    this.totalParticipants = this.totalRecords;
    this.activeParticipants = this.participants.filter(p => p.status !== null && p.status !== undefined && !p.isDeleted).length;
    this.inactiveParticipants = this.participants.filter(p => p.isDeleted).length;
    this.participantsWithoutStatus = this.participants.filter(p => p.status === null || p.status === undefined).length;
  }

  getRelativeTime(date: Date | string | undefined): string {
    if (!date) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInMs = now.getTime() - dateObj.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInDays > 0) {
      return `hace ${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
    } else if (diffInHours > 0) {
      return `hace ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    } else if (diffInMinutes > 0) {
      return `hace ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    } else {
      return 'hace un momento';
    }
  }
}
